import { useState, Fragment } from "react";
import { Plus, Pencil, Trash2, ChevronDown, ChevronRight, Grid3X3, Printer } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { useWarehouses, useDeleteWarehouse, useWarehouseBins, useCreateShelf, useDeleteBin, useDeleteShelf } from "@/hooks/use-warehouse";
import { WarehouseDialog } from "./warehouse-dialog";
import { toast } from "sonner";
import { openBlob } from "@/lib/utils";

function BinManager({ warehouseId }: { warehouseId: string }) {
  const { data: bins, isLoading } = useWarehouseBins(warehouseId);
  const createShelfMut = useCreateShelf();
  const deleteBinMut = useDeleteBin();
  const deleteShelfMut = useDeleteShelf();

  const [shelf, setShelf] = useState(1);
  const [columns, setColumns] = useState(5);
  const [rows, setRows] = useState(3);
  const [deleteShelfTarget, setDeleteShelfTarget] = useState<number | null>(null);
  const [deleteBinTarget, setDeleteBinTarget] = useState<{ id: number; label: string } | null>(null);
  const [printingShelf, setPrintingShelf] = useState<number | null>(null);

  async function handleCreateShelf(e: React.FormEvent) {
    e.preventDefault();
    try {
      const created = await createShelfMut.mutateAsync({ warehouseId, shelf, columns, rows });
      const count = (created as any[]).length;
      if (count > 0) {
        toast.success(`${count} lokacija kreirano na polici ${shelf}`);
      } else {
        toast.info("Sve lokacije već postoje");
      }
    } catch (e: any) {
      toast.error(e?.message || "Greška pri kreiranju police");
    }
  }

  async function handleDeleteShelf() {
    if (deleteShelfTarget === null) return;
    try {
      await deleteShelfMut.mutateAsync({ warehouseId, shelf: deleteShelfTarget });
      toast.success(`Polica ${deleteShelfTarget} obrisana`);
      setDeleteShelfTarget(null);
    } catch (e: any) {
      toast.error(e?.message || "Greška pri brisanju police");
    }
  }

  async function handleDeleteBin() {
    if (!deleteBinTarget) return;
    try {
      await deleteBinMut.mutateAsync(deleteBinTarget.id);
      toast.success(`Lokacija ${deleteBinTarget.label} obrisana`);
      setDeleteBinTarget(null);
    } catch (e: any) {
      toast.error(e?.message || "Greška pri brisanju lokacije");
    }
  }

  async function handlePrintStickers(shelfNum: number) {
    setPrintingShelf(shelfNum);
    try {
      const res = await fetch(`/api/warehouse/${warehouseId}/stickers/${shelfNum}`);
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Greška pri generisanju naljepnica");
      }
      const blob = await res.blob();
      openBlob(blob, `naljepnice-polica-${shelfNum}.pdf`);
    } catch (e: any) {
      toast.error(e?.message || "Greška pri generisanju naljepnica");
    } finally {
      setPrintingShelf(null);
    }
  }

  const gridBins = bins ? bins.filter((b: any) => b.shelf != null) : [];
  const shelves = [...new Set(gridBins.map((b: any) => b.shelf))].sort(
    (a: number, b: number) => a - b
  );


  return (
    <div className="flex flex-col gap-4 pl-4 border-l">
      <form onSubmit={handleCreateShelf} className="flex flex-wrap gap-2 items-end">
        <div className="flex flex-col gap-1">
          <Label className="text-xs">Polica</Label>
          <Input type="number" min={1} value={shelf} onChange={(e) => setShelf(Number(e.target.value))} className="w-20 h-8" />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-xs">Kolone</Label>
          <Input type="number" min={1} value={columns} onChange={(e) => setColumns(Number(e.target.value))} className="w-20 h-8" />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-xs">Redovi</Label>
          <Input type="number" min={1} value={rows} onChange={(e) => setRows(Number(e.target.value))} className="w-20 h-8" />
        </div>
        <Button type="submit" size="sm" disabled={createShelfMut.isPending}>
          <Grid3X3 className="h-3.5 w-3.5 mr-1" />
          Generiši {columns}×{rows}
        </Button>
      </form>

      {isLoading ? (
        <p className="text-xs text-muted-foreground">Učitavanje...</p>
      ) : !gridBins.length ? (
        <p className="text-xs text-muted-foreground">Nema lokacija</p>
      ) : (
        <TooltipProvider delayDuration={100}>
          <div className="flex flex-col gap-3">
            {shelves.map((s: number) => {
              const shelfBins = gridBins.filter((b: any) => b.shelf === s);
              const maxCol = Math.max(...shelfBins.map((b: any) => b.col));
              const maxRow = Math.max(...shelfBins.map((b: any) => b.row));
              return (
                <div key={s} className="flex flex-col gap-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium">
                      Polica {s}
                      <span className="text-muted-foreground font-normal ml-1.5">
                        {maxCol}×{maxRow} ({shelfBins.length})
                      </span>
                    </span>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="xs"
                        onClick={() => handlePrintStickers(s)}
                        disabled={printingShelf === s}
                      >
                        <Printer className="h-3 w-3 mr-1" />
                        Naljepnice
                      </Button>
                      <Button
                        variant="ghost"
                        size="xs"
                        className="text-destructive hover:text-destructive"
                        onClick={() => setDeleteShelfTarget(s)}
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Obriši
                      </Button>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <div
                      className="grid gap-0.5"
                      style={{
                        gridTemplateColumns: `repeat(${maxCol}, 20px)`,
                      }}
                    >
                      {Array.from({ length: maxRow }, (_, ri) =>
                        Array.from({ length: maxCol }, (_, ci) => {
                          const bin = shelfBins.find((b: any) => b.col === ci + 1 && b.row === ri + 1);
                          if (!bin) return <div key={`${ci}-${ri}`} className="h-5" />;
                          return (
                            <Tooltip key={bin.id}>
                              <TooltipTrigger asChild>
                                <button
                                  className="h-5 w-5 bg-foreground/80 hover:bg-destructive transition-colors rounded-[2px]"
                                  onClick={() => setDeleteBinTarget({ id: bin.id, label: bin.label })}
                                />
                              </TooltipTrigger>
                              <TooltipContent side="top" className="text-xs px-2 py-1">
                                {bin.label}
                              </TooltipContent>
                            </Tooltip>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </TooltipProvider>
      )}

      <Dialog open={deleteBinTarget !== null} onOpenChange={() => setDeleteBinTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Obriši lokaciju</DialogTitle>
            <DialogDescription>
              Jeste li sigurni da želite obrisati lokaciju {deleteBinTarget?.label}?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteBinTarget(null)}>Otkaži</Button>
            <Button variant="destructive" onClick={handleDeleteBin} disabled={deleteBinMut.isPending}>Obriši</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteShelfTarget !== null} onOpenChange={() => setDeleteShelfTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Obriši policu</DialogTitle>
            <DialogDescription>
              Jeste li sigurni da želite obrisati policu {deleteShelfTarget} i sve njene lokacije?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteShelfTarget(null)}>Otkaži</Button>
            <Button variant="destructive" onClick={handleDeleteShelf} disabled={deleteShelfMut.isPending}>Obriši</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function WarehouseSection() {
  const { data: warehouses, isLoading } = useWarehouses();
  const deleteMut = useDeleteWarehouse();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editWarehouse, setEditWarehouse] = useState<any>(null);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  function handleEdit(w: any) {
    setEditWarehouse(w);
    setDialogOpen(true);
  }

  function handleAdd() {
    setEditWarehouse(null);
    setDialogOpen(true);
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    try {
      await deleteMut.mutateAsync(deleteTarget.id);
      toast.success("Skladište obrisano");
      setDeleteTarget(null);
      if (expandedId === deleteTarget.id) setExpandedId(null);
    } catch (e: any) {
      toast.error(e?.message || "Greška pri brisanju skladišta");
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex justify-end">
        <Button size="sm" onClick={handleAdd} className="h-9">
          <Plus className="h-3.5 w-3.5 mr-1" /> Dodaj
        </Button>
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
        </div>
      ) : !warehouses?.length ? (
        <p className="text-xs text-muted-foreground py-8 text-center">Nema skladišta</p>
      ) : (
        <div className="ring-1 ring-foreground/10 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="p-2 w-8"></th>
                <th className="p-2 text-left font-medium">Naziv</th>
                <th className="p-2 text-left font-medium hidden sm:table-cell">Opis</th>
                <th className="p-2 text-left font-medium">Pozicija</th>
                <th className="p-2 text-right font-medium">Akcije</th>
              </tr>
            </thead>
            <tbody>
              {warehouses.map((w: any) => (
                <Fragment key={w.id}>
                  <tr className="border-b hover:bg-muted/50 transition-colors">
                    <td className="p-2">
                      <button
                        type="button"
                        className="p-0.5 hover:bg-muted transition-colors"
                        onClick={() => setExpandedId(expandedId === w.id ? null : w.id)}
                      >
                        {expandedId === w.id ? (
                          <ChevronDown className="h-3.5 w-3.5" />
                        ) : (
                          <ChevronRight className="h-3.5 w-3.5" />
                        )}
                      </button>
                    </td>
                    <td className="p-2 font-medium">{w.name}</td>
                    <td className="p-2 text-muted-foreground hidden sm:table-cell">{w.description || "—"}</td>
                    <td className="p-2 text-muted-foreground tabular-nums">{w.position}</td>
                    <td className="p-2">
                      <div className="flex gap-1 justify-end">
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(w)}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(w)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                  {expandedId === w.id && (
                    <tr className="border-b">
                      <td colSpan={5} className="p-4 bg-muted/30">
                        <BinManager warehouseId={w.id} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <WarehouseDialog open={dialogOpen} onOpenChange={setDialogOpen} warehouse={editWarehouse} />

      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Obriši skladište</DialogTitle>
            <DialogDescription>
              Jeste li sigurni da želite obrisati "{deleteTarget?.name}"? Sve lokacije i police će biti obrisane.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Otkaži</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleteMut.isPending}>Obriši</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
